/**
 * UGC video slider.
 *
 * Scrolling is native (CSS scroll-snap); this only drives the arrows, dots and
 * playback. That keeps touch momentum, trackpad gestures and keyboard scrolling
 * working for free, and the section stays usable as a plain scroller if this
 * module fails to load.
 */
class UgcVideoSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ugc-track]');
    if (!this.track) return;

    this.items = [...this.querySelectorAll('[data-ugc-item]')];
    this.isSlider = this.track.classList.contains('ugc__track--slider');
    this.autoplay = this.dataset.autoplay === 'true';
    this.loop = this.dataset.loop === 'true';

    this.addEventListener('click', this.#onClick);

    if (this.isSlider) {
      this.#buildDots();
      this.track.addEventListener('scroll', this.#onScroll, { passive: true });
      // Arrow state depends on measured widths, which aren't final until layout
      // settles (fonts, images). Re-measure on resize rather than caching once.
      this.resizeObserver = new ResizeObserver(() => this.#sync());
      this.resizeObserver.observe(this.track);
      this.#sync();
    }

    this.#setupPlayback();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.track?.removeEventListener('scroll', this.#onScroll);
    this.resizeObserver?.disconnect();
    this.io?.disconnect();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-ugc-prev]')) return this.#page(-1);
    if (target.closest('[data-ugc-next]')) return this.#page(1);

    const dot = target.closest('[data-ugc-dot]');
    if (dot instanceof HTMLElement) return this.#scrollToIndex(Number(dot.dataset.ugcDot));

    const sound = target.closest('[data-ugc-sound]');
    if (sound instanceof HTMLElement) return this.#toggleSound(sound);

    const play = target.closest('[data-ugc-play]');
    if (play instanceof HTMLElement) return this.#play(play);
  };

  /* ---------------- Slider ---------------- */

  #step() {
    const first = this.items[0];
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  /** @param {number} direction */
  #page(direction) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const next = this.track.scrollLeft + direction * this.#step() * this.#perView();

    if (this.loop) {
      // 2px tolerance: sub-pixel widths mean scrollLeft rarely lands exactly on
      // the maximum, so a strict comparison would never wrap.
      if (direction > 0 && this.track.scrollLeft >= max - 2) return this.#scrollTo(0);
      if (direction < 0 && this.track.scrollLeft <= 2) return this.#scrollTo(max);
    }

    this.#scrollTo(Math.max(0, Math.min(max, next)));
  }

  #perView() {
    const step = this.#step();
    return step > 0 ? Math.max(1, Math.round(this.track.clientWidth / step)) : 1;
  }

  /** @param {number} left */
  #scrollTo(left) {
    this.track.scrollTo({ left, behavior: 'smooth' });
  }

  /** @param {number} index */
  #scrollToIndex(index) {
    this.#scrollTo(index * this.#step());
  }

  #buildDots() {
    this.dots = this.querySelector('[data-ugc-dots]');
    if (!this.dots) return;

    this.dots.replaceChildren();
    // One dot per page, not per item - with four visible a dot each would be
    // a row of decorative dots that don't correspond to anything.
    const pages = Math.max(1, Math.ceil(this.items.length / this.#perView()));
    if (pages < 2) {
      this.dots.hidden = true;
      return;
    }
    this.dots.hidden = false;

    for (let i = 0; i < pages; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ugc__dot';
      dot.dataset.ugcDot = String(i * this.#perView());
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      this.dots.append(dot);
    }
  }

  #onScroll = () => {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const at = this.track.scrollLeft;

    const prev = this.querySelector('[data-ugc-prev]');
    const next = this.querySelector('[data-ugc-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = !this.loop && at <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = !this.loop && at >= max - 2;

    const dots = this.dots?.querySelectorAll('[data-ugc-dot]');
    if (!dots?.length) return;
    const step = this.#step() * this.#perView();
    const active = step > 0 ? Math.round(at / step) : 0;
    dots.forEach((d, i) => d.setAttribute('aria-selected', i === active ? 'true' : 'false'));
  }

  /* ---------------- Playback ---------------- */

  #setupPlayback() {
    const videos = this.querySelectorAll('video.ugc__video');
    if (!videos.length) return;

    // Honour the OS setting: autoplaying a wall of clips is exactly the kind of
    // motion this is meant to suppress.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.autoplay || reduced) return;

    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          const item = video.closest('[data-ugc-item]');
          if (entry.isIntersecting) {
            video.muted = true;
            // play() rejects when the browser blocks autoplay; swallow it or it
            // surfaces as an unhandled rejection on every card.
            video.play().then(
              () => item?.setAttribute('data-playing', ''),
              () => item?.removeAttribute('data-playing')
            );
          } else {
            video.pause();
            item?.removeAttribute('data-playing');
          }
        }
      },
      { threshold: 0.5 }
    );

    for (const video of videos) this.io.observe(video);
  }

  /** @param {HTMLElement} button */
  #play(button) {
    const item = button.closest('[data-ugc-item]');
    if (!item) return;

    const embed = item.querySelector('[data-ugc-embed]');
    if (embed instanceof HTMLElement) return this.#loadEmbed(embed);

    const video = item.querySelector('video.ugc__video');
    if (!(video instanceof HTMLVideoElement)) return;

    if (video.paused) {
      video.play().then(() => item.setAttribute('data-playing', ''), () => {});
    } else {
      video.pause();
      item.removeAttribute('data-playing');
    }
  }

  /**
   * Injects the YouTube/Vimeo iframe on demand. Nothing third-party is
   * requested until the customer asks for it.
   * @param {HTMLElement} embed
   */
  #loadEmbed(embed) {
    if (embed.dataset.loaded) return;

    const { embedType, embedId } = embed.dataset;
    if (!embedId) return;

    const src =
      embedType === 'vimeo'
        ? `https://player.vimeo.com/video/${embedId}?autoplay=1&muted=1&playsinline=1`
        : `https://www.youtube.com/embed/${embedId}?autoplay=1&mute=1&playsinline=1&rel=0`;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = 'Customer video';
    iframe.loading = 'lazy';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;

    embed.replaceChildren(iframe);
    embed.dataset.loaded = 'true';
    embed.closest('[data-ugc-item]')?.setAttribute('data-playing', '');
  }

  /** @param {HTMLElement} button */
  #toggleSound(button) {
    const item = button.closest('[data-ugc-item]');
    const video = item?.querySelector('video.ugc__video');
    if (!(video instanceof HTMLVideoElement)) return;

    const unmuting = video.muted;

    // Only one clip may be audible at a time - several playing at once is the
    // fastest way to make a customer leave the page.
    if (unmuting) {
      for (const other of this.querySelectorAll('video.ugc__video')) {
        if (other !== video) other.muted = true;
      }
      for (const other of this.querySelectorAll('[data-ugc-sound]')) {
        if (other !== button) {
          other.setAttribute('aria-pressed', 'false');
          other.setAttribute('aria-label', 'Unmute video');
        }
      }
    }

    video.muted = !unmuting;
    button.setAttribute('aria-pressed', String(unmuting));
    button.setAttribute('aria-label', unmuting ? 'Mute video' : 'Unmute video');

    if (unmuting && video.paused) video.play().catch(() => {});
  }
}

if (!customElements.get('ugc-video-slider')) {
  customElements.define('ugc-video-slider', UgcVideoSlider);
}
